import React from 'react';
import { LuLoader } from 'react-icons/lu';
import { Area, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { DateStr, PerformanceData } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { formatCurrency, formatCurrencyShort, formatDate } from 'utils';

import {
    NAV_CHART_AREA_FILL,
    NAV_CHART_AXIS_TICK_COLOR,
    NAV_CHART_AXIS_TICK_FONT_SIZE,
    NAV_CHART_DASH_PATTERN,
    NAV_CHART_GRID_COLOR,
    NAV_CHART_LINE_COLOR,
    NAV_CHART_LINE_WIDTH,
    NAV_CHART_MONTH_LABEL_FORMAT,
    NAV_CHART_Y_PADDING,
} from '../constants';

interface NavChartOverlayBase {
    // Unique id for the chart series; must not collide across overlays.
    key: string;
    label: string;
    color: string;
    // Dashed line (comparison series that are not part of the account's own history).
    dashed?: boolean;
    // Appended to the footnote while this overlay is drawn (deduplicated across overlays).
    disclosure?: string;
    // The overlay's series is still being fetched; the legend shows a spinner so an empty
    // line reads as "loading", not "broken".
    isPending?: boolean;
}

// A comparison series read straight off the PerformanceData rows (reportDate is the category axis).
export interface PerformanceFieldOverlay extends NavChartOverlayBase {
    source: 'performance';
    field: Exclude<keyof PerformanceData, 'reportDate'>;
}

// A comparison series supplied from outside the PerformanceData rows (e.g. benchmark
// comparison), merged into the chart by reportDate. Dates absent from the map render as
// gaps, so a series whose history starts mid-window simply begins at its first point.
export interface ExternalSeriesOverlay extends NavChartOverlayBase {
    source: 'external';
    values: ReadonlyMap<DateStr, number>;
}

export type NavChartOverlay = PerformanceFieldOverlay | ExternalSeriesOverlay;

interface NavChartProps {
    data?: PerformanceData[];
    isLoading?: boolean;
    // Every overlay is drawn: net invested capital always, the benchmarks picked in the header.
    overlays: NavChartOverlay[];
}

// Missing values become NaN (recharts leaves a gap) — Number(null) would plot a false $0.
export const toChartValue = (value: string | null | undefined): number => (value == null ? NaN : Number(value));

const overlayValue = (overlay: NavChartOverlay, point: PerformanceData): number =>
    overlay.source === 'performance'
        ? toChartValue(point[overlay.field])
        : (overlay.values.get(point.reportDate) ?? NaN);

// Legend swatch mirroring the line style: solid bar, or dashed rule for dashed overlays.
// Sized like NetValueCard's legend swatch (w-4, 2px).
const LineSwatch = ({ color, dashed, className }: { color: string; dashed?: boolean; className?: string }) =>
    dashed ? (
        <span
            className={cn('w-4 shrink-0 self-center border-t-2 border-dashed', className)}
            style={{ borderColor: color }}
        />
    ) : (
        <span className={cn('h-0.5 w-4 shrink-0 self-center', className)} style={{ backgroundColor: color }} />
    );

// Clamp both ends at zero (market value is never plotted below it), then pad by a share of the
// visible range. A flat series (zero range) pads by a share of the value instead, and an all-zero
// series by a share of 1, so the axis always has some height and never inverts.
export const navChartDomain = ([dataMin, dataMax]: readonly [number, number]): [number, number] => {
    const floor = Math.max(dataMin, 0);
    const ceiling = Math.max(dataMax, 0);
    const padding = NAV_CHART_Y_PADDING * (ceiling - floor || ceiling || 1);
    return [Math.max(floor - padding, 0), ceiling + padding];
};

export const NavChart = ({ data, isLoading, overlays }: NavChartProps) => {
    // Keep the raw YYYY-MM-DD(...) string as the category key (it sorts chronologically).
    const chartData = (data ?? [])
        .map((point) => ({
            date: point.reportDate,
            marketValue: toChartValue(point.marketValue),
            ...Object.fromEntries(overlays.map((overlay) => [overlay.key, overlayValue(overlay, point)])),
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

    // One tick per calendar month: the first data point in each month (YYYY-MM).
    const monthTicks = chartData.reduce<string[]>((ticks, point) => {
        const monthKey = point.date.slice(0, 7);
        const lastMonthKey = ticks.length ? ticks[ticks.length - 1].slice(0, 7) : null;
        if (monthKey !== lastMonthKey) {
            ticks.push(point.date);
        }
        return ticks;
    }, []);

    const disclosures = [
        ...new Set(overlays.map((overlay) => overlay.disclosure).filter((line): line is string => !!line)),
    ];

    // Card shell and title match the account page's NetValueCard.
    return (
        <Card className="border-border flex min-w-0 flex-col rounded-2xl bg-white p-4 lg:p-6">
            <h3 className="font-heading text-cocoa-1000 text-xl">Market Value</h3>
            <div className="mt-6 h-72 w-full">
                {isLoading ? (
                    <div className="bg-cocoa-100 h-full w-full animate-pulse rounded-md" />
                ) : chartData.length === 0 ? (
                    <div className="text-cocoa-400 flex h-full w-full items-center justify-center text-sm">
                        No performance history available.
                    </div>
                ) : (
                    <ComposedChart
                        className="[&_*]:!outline-none"
                        responsive
                        style={{ width: '100%', height: '100%' }}
                        data={chartData}
                        margin={{ top: 8, right: 8, left: 16, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={NAV_CHART_GRID_COLOR} vertical={false} />
                        <XAxis
                            dataKey="date"
                            ticks={monthTicks}
                            tickFormatter={(d: string) => formatDate(d, NAV_CHART_MONTH_LABEL_FORMAT)}
                            tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            minTickGap={24}
                        />
                        <YAxis
                            orientation="right"
                            domain={navChartDomain}
                            tickFormatter={(v: number) => formatCurrencyShort(v)}
                            tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                            axisLine={false}
                            tickLine={false}
                            width={72}
                        />
                        <Tooltip
                            formatter={(value, name) => [formatCurrency(Array.isArray(value) ? value[0] : value), name]}
                            labelFormatter={(d) => formatDate(String(d), 'HUMAN')}
                            contentStyle={{
                                fontSize: 12,
                                borderRadius: 6,
                                border: `1px solid ${NAV_CHART_GRID_COLOR}`,
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="marketValue"
                            name="Market Value"
                            stroke={NAV_CHART_LINE_COLOR}
                            strokeWidth={NAV_CHART_LINE_WIDTH}
                            fill={NAV_CHART_AREA_FILL}
                            fillOpacity={1}
                            dot={false}
                            activeDot={{ r: 4, fill: NAV_CHART_LINE_COLOR }}
                        />
                        {overlays.map((overlay) => (
                            <Line
                                key={overlay.key}
                                type="monotone"
                                // Function accessor: overlay keys (e.g. benchmark names) may contain
                                // dots, which a string dataKey would misread as a nested path.
                                dataKey={(point: Record<string, number>) => point[overlay.key]}
                                name={overlay.label}
                                stroke={overlay.color}
                                strokeWidth={NAV_CHART_LINE_WIDTH}
                                strokeDasharray={overlay.dashed ? NAV_CHART_DASH_PATTERN : undefined}
                                // Recharts animates the draw-in via stroke-dasharray, which
                                // overrides the dash pattern and flashes the line solid.
                                isAnimationActive={false}
                                dot={false}
                                activeDot={{ r: 4, fill: overlay.color }}
                            />
                        ))}
                    </ComposedChart>
                )}
            </div>
            {/* Legend under the chart, as on External flows (NetInflowOutflow); labels use the account
                page's NetValueCard legend style (10px tracked uppercase). */}
            <div className="mt-3 flex flex-wrap items-center gap-6 text-[10px] font-semibold tracking-wider uppercase">
                <div className="text-cocoa-500 flex items-center gap-2">
                    <LineSwatch color={NAV_CHART_LINE_COLOR} />
                    Market Value
                </div>
                {overlays.map((overlay) => (
                    <div key={overlay.key} className="flex items-center gap-2">
                        <LineSwatch color={overlay.color} dashed={overlay.dashed} />
                        <span className={overlay.isPending ? 'text-cocoa-400' : 'text-cocoa-500'}>{overlay.label}</span>
                        {overlay.isPending ? <LuLoader className="text-cocoa-400 h-3.5 w-3.5 animate-spin" /> : null}
                    </div>
                ))}
            </div>
            <div className="text-cocoa-400 mt-2 text-xs">
                {['Time-weighted returns, net of all fees and reflecting reinvested dividends.', ...disclosures].join(
                    ' ',
                )}
            </div>
        </Card>
    );
};
